// apps/admin/src/components/notification-bell.tsx
//
// P5 UI Polish W1 T4 — Notification bell + unread feed (spec §2.2).
//
// Subscribes to adminWs.onAuditEventCreated AND
// adminWs.onVerificationFailedAlert (verification-failures last mile);
// buffers the last 10 events in component state. Verification failures
// are security-relevant, so they additionally fire an error toast.
// Unread counter computes against a "last viewed" timestamp persisted
// to localStorage. Opening the panel updates the last-viewed timestamp,
// clearing the unread badge.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icon';
import { adminWs } from '@/api/ws-admin';
import { toast } from '@/lib/toast';

const BUFFER_SIZE = 10;
const LAST_VIEWED_KEY = 'displaycms.admin.notifications.lastViewed';

interface NotificationItem {
  id: string;
  kind: 'audit' | 'verification_failure';
  receivedAtMs: number;
  resourceVersionId: string | null;
  composerVersionId: string | null;
  playerId: string | null;
  failureCount: number | null;
}

export function NotificationBell(): JSX.Element {
  const { t } = useTranslation(['common']);
  const tRef = useRef(t);
  tRef.current = t;
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [open, setOpen] = useState(false);
  const [lastViewed, setLastViewed] = useState<number>(() => {
    const raw = localStorage.getItem(LAST_VIEWED_KEY);
    return raw ? Number(raw) : 0;
  });
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    adminWs.connect();
    const offAudit = adminWs.onAuditEventCreated((evt) => {
      setItems((prev) => {
        const next: NotificationItem = {
          id: evt.audit_event_id,
          kind: 'audit',
          receivedAtMs: Date.now(),
          resourceVersionId: evt.resource_version_id ?? null,
          composerVersionId: evt.composer_version_id ?? null,
          playerId: null,
          failureCount: null,
        };
        return [next, ...prev].slice(0, BUFFER_SIZE);
      });
    });
    // Verification-failures last mile — security-relevant, so the alert
    // both joins the bell feed and fires an error toast immediately.
    const offVerification = adminWs.onVerificationFailedAlert((evt) => {
      const receivedAtMs = Date.now();
      setItems((prev) => {
        const next: NotificationItem = {
          // The alert carries no event id; player + receive time is unique
          // enough for a 10-item ephemeral feed.
          id: `verification-${evt.player_id}-${receivedAtMs}`,
          kind: 'verification_failure',
          receivedAtMs,
          resourceVersionId: null,
          composerVersionId: null,
          playerId: evt.player_id,
          failureCount: evt.failures.length,
        };
        return [next, ...prev].slice(0, BUFFER_SIZE);
      });
      toast.error(
        tRef.current('common:verification_failed_alert', {
          count: evt.failures.length,
          player: evt.player_id.slice(0, 8),
        }),
      );
    });
    return () => {
      offAudit();
      offVerification();
    };
  }, []);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onClickOutside);
    return () => window.removeEventListener('mousedown', onClickOutside);
  }, []);

  const unreadCount = items.filter((it) => it.receivedAtMs > lastViewed).length;

  const handleOpen = (): void => {
    setOpen((o) => {
      const willOpen = !o;
      if (willOpen) {
        const now = Date.now();
        setLastViewed(now);
        localStorage.setItem(LAST_VIEWED_KEY, String(now));
      }
      return willOpen;
    });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="notification-bell"
        onClick={handleOpen}
        className="relative flex h-9 w-9 items-center justify-center rounded-md hover:bg-muted"
        aria-label={t('common:notifications', { defaultValue: 'Notifications' })}
      >
        <Icon name="bell" size={18} />
        {unreadCount > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-xs text-destructive-foreground"
          >
            {unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 mt-2 w-80 rounded-md border border-border bg-card p-2 shadow-lg z-30"
        >
          {items.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t('common:notifications_empty', { defaultValue: 'Aucune notification récente.' })}
            </p>
          ) : (
            <ul className="max-h-80 overflow-auto">
              {items.map((it) => (
                <li key={it.id} className="border-b border-border py-2 px-2 last:border-0">
                  <div className="text-xs text-muted-foreground">
                    {new Date(it.receivedAtMs).toLocaleString()}
                  </div>
                  {it.kind === 'verification_failure' ? (
                    <div
                      className="text-sm text-destructive"
                      data-testid="notification-verification-failure"
                    >
                      {t('common:verification_failed_alert', {
                        count: it.failureCount ?? 0,
                        player: (it.playerId ?? '').slice(0, 8),
                      })}
                    </div>
                  ) : (
                    <div className="text-sm">
                      {it.resourceVersionId
                        ? `Resource ${it.resourceVersionId.slice(0, 8)}…`
                        : it.composerVersionId
                          ? `Composer ${it.composerVersionId.slice(0, 8)}…`
                          : it.id.slice(0, 8) + '…'}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
